import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

/**
 * itineraryDuplicateTargetSchema is the itinerary the duplicate form is open for.
 * That id is all the store holds: the name, the target organization, and the kept
 * segment/waypoint selections stay in the form's own state, so the slot is a minimal
 * open/closed pointer rather than a mirror of a whole form. Zod-first because the
 * value is shared with the duplicate-open tool input; a null target (form closed)
 * lives outside the schema as the absence of a value.
 */
export const itineraryDuplicateTargetSchema = z.object({
  itineraryId: z.string(),
});

/**
 * ItineraryDuplicateTarget is the itinerary the duplicate form is open for, derived
 * from {@link itineraryDuplicateTargetSchema}. The slot holds this or null (closed).
 */
export type ItineraryDuplicateTarget = z.infer<typeof itineraryDuplicateTargetSchema>;

/**
 * ItineraryDuplicateSliceState is the open duplicate-itinerary form slot.
 */
export interface ItineraryDuplicateSliceState {
  /**
   * itineraryDuplicate is the open duplicate form's state (null = the form is
   * closed). It holds only the itinerary being copied; everything the user chooses
   * lives in the form until it is submitted. Set only through the tool bus, like
   * every other UiAction.
   */
  itineraryDuplicate: ItineraryDuplicateTarget | null;
}

/**
 * ItineraryDuplicateSliceActions is the whole-slot setter that drives the duplicate
 * form, reached only through the tool bus.
 */
export interface ItineraryDuplicateSliceActions {
  /**
   * setItineraryDuplicate opens the duplicate form with the given target, or closes
   * it when passed null. The whole-slot setter the open/close tools drive, mirroring
   * setShare.
   */
  setItineraryDuplicate: (target: ItineraryDuplicateTarget | null) => void;
}

/**
 * itineraryDuplicateInitialState seeds the duplicate form closed.
 */
export const itineraryDuplicateInitialState: ItineraryDuplicateSliceState = {
  itineraryDuplicate: null,
};

/**
 * createItineraryDuplicateSlice builds the duplicate form action. The single
 * whole-slot setter opens the form for an itinerary or closes it, mirroring the share
 * and directions slices' open/close shape.
 */
export const createItineraryDuplicateSlice: StateCreator<
  ItineraryDuplicateSliceState & ItineraryDuplicateSliceActions,
  [],
  [],
  ItineraryDuplicateSliceActions
> = (set) => ({
  setItineraryDuplicate: (itineraryDuplicate) => set({ itineraryDuplicate }),
});

/**
 * selectItineraryDuplicate is the open duplicate form's target, or null when it is
 * closed. The mount-guard selector the modal reads to decide whether to render at all
 * — narrow so the guard re-renders only on the open/close flip, mirroring selectShare.
 */
export const selectItineraryDuplicate = (
  state: ItineraryDuplicateSliceState,
): ItineraryDuplicateTarget | null => state.itineraryDuplicate;
